import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, GraduationCap, MapPin, Clock, ChevronLeft, Star, HardHat, Calculator, Cpu, Users } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { COMPANY_INFO } from '@/lib/index';

const JOBS = [
  { icon: HardHat, title: 'مهندس مدني أول', dept: 'الهندسة الإنشائية', type: 'دوام كامل', location: 'المدينة المنورة', exp: '5+ سنوات', skills: ['الهياكل الخرسانية', 'البرامج الهندسية', 'إدارة المواقع'], urgent: true },
  { icon: Calculator, title: 'مهندس مساحة وتكاليف', dept: 'إدارة التكاليف', type: 'دوام كامل', location: 'المدينة المنورة', exp: '3+ سنوات', skills: ['حصر الكميات', 'تحليل التكاليف', 'BOQ'], urgent: false },
  { icon: Cpu, title: 'مهندس كهرباء', dept: 'الأعمال الكهربائية', type: 'دوام كامل', location: 'المدينة المنورة', exp: '4+ سنوات', skills: ['M&E Systems', 'أنظمة الحماية', 'الإنارة الفندقية'], urgent: true },
  { icon: HardHat, title: 'مشرف موقع', dept: 'تنفيذ المشاريع', type: 'دوام كامل', location: 'المدينة المنورة', exp: '3+ سنوات', skills: ['إدارة العمالة', 'الجودة والسلامة', 'تقارير التقدم'], urgent: false },
  { icon: Users, title: 'مدير مشروع', dept: 'إدارة المشاريع', type: 'دوام كامل', location: 'المدينة المنورة', exp: '8+ سنوات', skills: ['PMP معتمد', 'MS Project', 'قيادة الفرق'], urgent: false },
  { icon: GraduationCap, title: 'مهندس معماري', dept: 'التصميم والتشطيب', type: 'دوام كامل', location: 'المدينة المنورة', exp: '3+ سنوات', skills: ['AutoCAD', 'Revit', 'التصميم الداخلي'], urgent: false },
];

const BENEFITS = [
  { icon: '💰', title: 'رواتب تنافسية', desc: 'رواتب مجزية تعكس مستوى الخبرة والكفاءة' },
  { icon: '🏨', title: 'سكن وتنقل', desc: 'توفير السكن أو بدل السكن وخدمات التنقل' },
  { icon: '📈', title: 'تطوير مهني', desc: 'برامج تدريبية وفرص للنمو الوظيفي المستمر' },
  { icon: '🏥', title: 'تأمين صحي', desc: 'تأمين صحي شامل للموظف وأسرته' },
  { icon: '✈️', title: 'تذاكر سفر', desc: 'تذاكر سنوية للعودة إلى الوطن' },
  { icon: '🏆', title: 'بيئة متميزة', desc: 'بيئة عمل احترافية في مشاريع فاخرة فريدة' },
];

export default function Careers() {
  const [selected, setSelected] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', position: '', experience: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: '', phone: '', email: '', position: '', experience: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/luxury_hotel_building_construction_site__5.jpeg" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-navy/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/30 rounded-full text-gold text-sm mb-6">
              <Briefcase size={14} /><span>الوظائف والتوظيف</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              انضم إلى <span className="text-gold">فريق النخبة</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed">
              نبحث عن المهندسين والفنيين المتميزين لبناء أفخم الفنادق في المدينة المنورة. هل أنت مستعد للتحدي؟
            </p>
          </motion.div>
        </div>
      </section>

      {/* Jobs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              الوظائف <span className="text-gold">المتاحة</span>
            </h2>
            <p className="text-muted-foreground">فرص حقيقية للعمل في مشاريع فندقية فاخرة بالمدينة المنورة</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {JOBS.map((job, i) => {
              const Icon = job.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.07 }} viewport={{ once: true }}
                  className="bg-white border border-border rounded-2xl p-6 hover:border-navy/30 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  onClick={() => setSelected(i)}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-navy/8 rounded-xl flex items-center justify-center group-hover:bg-navy transition-colors duration-300">
                      <Icon size={22} className="text-navy group-hover:text-white transition-colors" />
                    </div>
                    {job.urgent && (
                      <span className="px-2.5 py-1 bg-red-50 text-red-600 border border-red-100 text-xs rounded-full font-semibold">عاجل</span>
                    )}
                  </div>
                  <h3 className="text-navy font-black text-lg mb-1 group-hover:text-gold transition-colors">{job.title}</h3>
                  <p className="text-muted-foreground text-xs mb-3">{job.dept}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={11} />{job.type}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={11} />{job.location}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Star size={11} />{job.exp}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {job.skills.map((s, j) => (
                      <span key={j} className="px-2 py-0.5 bg-navy/8 text-navy text-xs rounded-full">{s}</span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-navy mb-2" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              مزايا <span className="text-gold">العمل معنا</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: i * 0.06 }} viewport={{ once: true }}
                className="bg-white border border-border rounded-2xl p-5 flex items-start gap-4 hover:border-navy/25 hover:shadow-md transition-all duration-200">
                <div className="text-3xl flex-shrink-0">{b.icon}</div>
                <div>
                  <h4 className="text-navy font-bold text-sm mb-1">{b.title}</h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              قدّم <span className="text-gold">طلبك الآن</span>
            </h2>
            <p className="text-muted-foreground">أرسل بياناتك وسنتواصل معك في أقرب وقت</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-border p-8">
            <AnimatePresence>
              {sent && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                  ✅ تم استلام طلبك! سنتواصل معك خلال 3 أيام عمل.
                </motion.div>
              )}
            </AnimatePresence>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الاسم الكامل *</label>
                  <input required value={form.name} onChange={e => set('name', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الجوال *</label>
                  <input required type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} dir="ltr"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">البريد الإلكتروني *</label>
                <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} dir="ltr"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الوظيفة المطلوبة</label>
                  <select value={form.position} onChange={e => set('position', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر الوظيفة</option>
                    {JOBS.map(j => <option key={j.title}>{j.title}</option>)}
                    <option>أخرى</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">سنوات الخبرة</label>
                  <select value={form.experience} onChange={e => set('experience', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر</option>
                    <option>حديث التخرج</option><option>1 - 3 سنوات</option><option>3 - 5 سنوات</option><option>5 - 10 سنوات</option><option>أكثر من 10 سنوات</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">نبذة مختصرة عن خبرتك</label>
                <textarea rows={4} value={form.message} onChange={e => set('message', e.target.value)} placeholder="اذكر أبرز مشاريعك وإنجازاتك..."
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all resize-none" />
              </div>
              <button type="submit"
                className="w-full py-4 bg-navy text-white rounded-xl font-bold text-base hover:bg-navy-light transition-all hover:shadow-lg flex items-center justify-center gap-2">
                تقديم الطلب <ChevronLeft size={18} />
              </button>
              <p className="text-center text-xs text-muted-foreground">
                للاستفسار: <a href={`tel:${COMPANY_INFO.phone}`} className="text-gold font-semibold" dir="ltr">{COMPANY_INFO.phone}</a>
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* Job detail modal */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-navy font-black text-xl">{JOBS[selected].title}</h3>
                  <p className="text-muted-foreground text-sm">{JOBS[selected].dept}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-navy p-1">✕</button>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-2 text-sm"><Clock size={15} className="text-gold" />{JOBS[selected].type}</div>
                <div className="flex items-center gap-2 text-sm"><MapPin size={15} className="text-gold" />{JOBS[selected].location}</div>
                <div className="flex items-center gap-2 text-sm"><Star size={15} className="text-gold" />خبرة {JOBS[selected].exp}</div>
              </div>
              <div className="flex flex-wrap gap-2 mb-5">
                {JOBS[selected].skills.map((s, i) => <span key={i} className="px-3 py-1 bg-navy/8 text-navy text-xs rounded-full font-medium">{s}</span>)}
              </div>
              <button onClick={() => { setSelected(null); document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="w-full py-3 bg-navy text-white rounded-xl font-bold hover:bg-navy-light transition-all">
                تقديم على هذه الوظيفة
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
